import { Collection } from 'firestorter';
import Collections from '../utils/collections';
import BaseDocument from './BaseDocument';

/**
 * Instagram account status values
 * @enum {string}
 */
export const accountStatus = {
  AUTHORIZED: 'authorized',
  UNAUTHORIZED: 'unauthorized',
};

/**
 * Instagram permissions values
 * @enum {string}
 */
export const instagramPermissions = {
  INSTAGRAM_BUSINESS_BASIC: 'instagram_business_basic',
  INSTAGRAM_BUSINESS_MANAGE_MESSAGES: 'instagram_business_manage_messages',
};

class InstagramUser extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.INSTAGRAM_USER}/${id}`, opts);
  }

  /**
   * Get the account's status
   * @typedef {accountStatus.AUTHORIZED | accountStatus.UNAUTHORIZED} AccountStatus
   * @returns {AccountStatus} The account status
   */
  get accountStatus() {
    return this.data.accountStatus;
  }

  /**
   * Get the account's username
   * @returns {string} The account's instagram handle
   */
  get username() {
    return this.data.username;
  }

  /**
   * Get the account name
   * @returns {string} The account's instagram name
   */
  get name() {
    return this.data.name;
  }

  /**
   * Get the account's profile picture
   * @returns {string} The profile picture URL
   */
  get profilePictureUrl() {
    return this.data.profilePictureUrl;
  }

  /**
   * Get the associated coach ID
   * @returns {string | undefined} The associated coach ID
   */
  get coachId() {
    return this.data.coachId;
  }

  /**
   * Get the granted permissions
   * @returns {string[]} The granted permissions
   */
  get grantedPermissions() {
    return this.data.grantedPermissions || [];
  }

  get isTextSetterEnabled() {
    return !!this.data.isTextSetterEnabled;
  }

  /**
   * Update the text setter messaging enabled
   * @param {boolean} enabled - The text setter messaging enabled
   * @param {string} [lastUpdatedByUserName] - The user name of who enabled the text setter messaging
   * @returns {Promise<void>}
   */
  toggleTextSetter(enabled, lastUpdatedByUserName) {
    return this.updateFields({
      isTextSetterEnabled: enabled,
      ...(lastUpdatedByUserName && { lastUpdatedByUserName }),
    });
  }

  static async getInstaUserById(documentId) {
    const instagramUser = new InstagramUser(documentId);
    await instagramUser.init();

    return instagramUser.exists ? instagramUser : null;
  }

  static async getInstaUserByCoachId(coachId) {
    const instgramUserCollection = new Collection(Collections.INSTAGRAM_USER, {
      createDocument: ({ id }, opts) => new InstagramUser(id, opts),
      query: (ref) => ref.where('coachId', '==', coachId).limit(1),
    });
    await instgramUserCollection.fetch();
    return instgramUserCollection;
  }

  static async getSecondaryInstaUserByCoachId(coachId) {
    const collection = new Collection(Collections.INSTAGRAM_USER, {
      createDocument: ({ id }, opts) => new InstagramUser(id, opts),
      query: (ref) => ref.where('secondaryCoachId', '==', coachId).limit(1),
    });
    await collection.fetch();
    return collection;
  }
}

export default InstagramUser;
